import '.g{display:flex;}.ssr-plugin'
import '.iq{max-height:17rem;}.ssr-plugin'
import '.a1{flex-direction:row;}.ssr-plugin'
import '.p{gap:1rem;}.ssr-plugin'
import '.v{position:absolute;}.ssr-plugin'
import '.ir{left:-9999px;}.ssr-plugin'
import '.du{opacity:0;}.ssr-plugin'
import '.is{pointer-events:none;}.ssr-plugin'
import '.be{height:100%;}.ssr-plugin'
import '.a2{width:100%;}.ssr-plugin'
import '.h{flex-direction:column;}.ssr-plugin'
import '.i{align-items:center;}.ssr-plugin'
import '.k{justify-content:center;}.ssr-plugin'
import '.ci{border-radius:0.625rem;}.ssr-plugin'
import '.cg{aspect-ratio:1/1;}.ssr-plugin'
import '.it{color:#fff;}.ssr-plugin'
import '.az{font-family:FunnelDisplay;}.ssr-plugin'
import '.eh{font-weight:700;}.ssr-plugin'
import '.b1{font-style:normal;}.ssr-plugin'
import '.iu{font-size:3rem;}.ssr-plugin'
import '.iv{line-height:100%;}.ssr-plugin'
import '.b0{font-weight:400;}.ssr-plugin'
import '.br{font-size:0.875rem;}.ssr-plugin'
import '.fv{margin-top:0.5rem;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import Row from '@app/components/layout/Row'
import { useState, useCallback } from 'react'
import { CloseButton } from '@app/components/core/button/CloseButton'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import { ImageDisplay } from './blocks/ImageDisplay'
import { ImageOveriew } from './blocks/ImageOverview'
import { ImagePreview } from './blocks/ImagePreview'

export type PostMediaGalleryProps = {
  images: string[]
  overviewRenderContainer?: HTMLElement
}

export const PostMediaGallery = ({
  images,
  overviewRenderContainer,
}: PostMediaGalleryProps) => {
  // Always call hooks first
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.mediaGallery
  const [displayedIndex, setDisplayedIndex] = useState<number>(null)
  const [overviewShown, setOverviewShown] = useState(false)

  const renderImagePreview = useCallback(
    (index: number, wrapperStyle?: Style) => {
      return (
        <ImagePreview
          image={images[index]}
          index={index}
          wrapperStyle={{
            minWidth: 0,
            ...wrapperStyle,
          }}
          imageStyle={{
            borderRadius: '0.625rem',
            maxWidth: '100%',
          }}
          onImageClick={setDisplayedIndex}
        />
      )
    },
    [images],
  )

  const modalHeader = useCallback((onClose: () => void) => (
    <Row
      style={{
        flexDirection: 'row-reverse',
        alignItems: 'center',
      }}
    >
      <CloseButton onClick={onClose} />
    </Row>
  ), [])

  const renderOveriew = useCallback(() => {
    return (
      <ImageOveriew
        images={images}
        header={modalHeader(() => setOverviewShown(false))}
        onImageClick={setDisplayedIndex}
      />
    )
  }, [images, modalHeader])

  const goToOverview = useCallback(() => {
    setOverviewShown(true)
    setDisplayedIndex(null)
  }, [])

  const renderDisplay = useCallback(() => {
    return (
      <ImageDisplay
        displayedImage={images[displayedIndex]}
        overviewButtonLabel={translations.allMedia}
        header={modalHeader(() => setDisplayedIndex(null))}
        canGoBack={displayedIndex !== 0}
        canGoForwad={displayedIndex !== images.length - 1}
        goBack={() => setDisplayedIndex((prev) => prev - 1)}
        goForward={() => setDisplayedIndex((prev) => prev + 1)}
        goToOverview={images.length > 1 ? goToOverview : undefined}
      />
    )
  }, [images, displayedIndex, translations.allMedia, modalHeader, goToOverview])

  const renderModal = useCallback(() => {
    if (displayedIndex === null && !overviewShown) {return null}
    return (
      <ModalWrapper
        renderContainer={overviewRenderContainer}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6.5px)',
          overflowY: 'scroll',
          alignItems: displayedIndex !== null ? 'center' : 'flex-start',
        }}
      >
        {displayedIndex !== null ? renderDisplay() : renderOveriew()}
      </ModalWrapper>
    )
  }, [displayedIndex, overviewShown, renderDisplay, renderOveriew, overviewRenderContainer])

  // Early return after all hooks
  if (images.length === 0) {return null}

  if (images.length === 1) {
    return (
      <>
        {renderModal()}
        <div className="g iq"
        >
          {renderImagePreview(0)}
        </div>
      </>
    )
  }

  if (images.length < 4) {
    return (
      <>
        {renderModal()}
        <div className="iq g a1 p"
        >
          {renderImagePreview(0, { aspectRatio: '1/1' })}
          {images.length > 1 && renderImagePreview(1, { aspectRatio: '1/1' })}
          {images.length > 2 && renderImagePreview(2, { aspectRatio: '1/1' })}
          {images.length > 2 && (
            <div className="v ir du is"
              onClick={() => setOverviewShown(true)}
            >
              {translations.allMedia}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {renderModal()}
      <div className="iq g a1 p"
      >
        {renderImagePreview(0, { aspectRatio: '1/1' })}
        {renderImagePreview(1, { aspectRatio: '1/1' })}
        {images.slice(2).map((_, index) => renderImagePreview(index + 2, { display: 'none' }))}
        <div className="be a2 g h i k ci cg"
          onClick={() => setOverviewShown(true)}
          style={{
            background: `linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${images[2]}) lightgray 50% / cover no-repeat`,
          }}
        >
          <div className="it az eh b1 iu iv"
          >
            +{images.length - 2}
          </div>
          <div className="it az b0 br fv"
          >
            {translations.allMedia}
          </div>
        </div>
      </div>
    </>
  )
}
